import { FunctionComponent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import ConnectionTable from '../components/connections/ConnectionTable';
import Column from '../../../components/Column';
import ConnectionControlBar from '../components/connections/ConnectionControlBar';
import { IConnection } from '../../../interfaces/models/connection';
import KanbanBoard from './kanban/KanbanBoard';
import { useConnectionsHandlers } from '../../../services/actions/connections/useConnectionsHandlers';
import { EnrichmentType } from '../components/connections/EnrichmentTypeEnum';
import { useAppHandlers } from '../../../services/actions/app/useAppHandlers';
import TabsView, { ITabProperties } from '../../../components/TabsView';
import { IEnrichedTransaction } from '../../../components/modal/modals/extension/CalculateEnrichmentModal';
import { useConnectionsStore } from '../../../stores/connections/useConnectionsStore';
import QuickActions from '../components/connections/QuickActions';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/auth/useAuthStore';

interface IConnectionSectionProps { }

const ConnectionSection: FunctionComponent<IConnectionSectionProps> = () => {
    const location = useLocation()
    const { store: { workspace } } = useAuthStore()
    const navigate = useNavigate()
    const { showCreateFeedGuide, showCreateFeedModal, selectedConnectionId, fromTagsPage } = location.state || {}

    const [selectedConnections, setSelectedConnections] = useState<IConnection[]>([]);
    const [selectedAllConnections, setSelectedAllConnections] = useState<boolean>(false);
    const [controlBarHeight, setControlBarHeight] = useState<number>(0);
    const controlBarRef = useRef<HTMLDivElement>(null);
    const [enrichingItems, setEnrichingItems] = useState<Array<{connectionId: string, type: EnrichmentType}>>([]);
    const [enrichingTransactionId, setEnrichingTransactionId] = useState<string>('');
    const [enrichedTransaction, setEnrichedTransaction] = useState<IEnrichedTransaction | undefined>(undefined);
    const { setSelectedConnectionsIdsHandler, calculateEnrichmentHandler, setConnectionsParametersHandler, getCustomFieldDefinitionsHandler } = useConnectionsHandlers();
    const { setCalculateEnrichmentModalHandler, setCreateFeedGuideModal, setCreateFeedModal } = useAppHandlers();
    const { store: { connectionsParameters, connections } } = useConnectionsStore();

    const [kanbanView, setKanbanView] = useState(() => {
        const saved = localStorage.getItem('kanbanView');
        return saved === 'true';
    });
    
    const guideTimerRef = useRef<NodeJS.Timeout | null>(null);
    const hasProcessedGuide = useRef(false);
    const hasProcessedFromTagsPage = useRef(false);

    const updateControlBarHeight = () => {
        if (controlBarRef.current) {
            const height = controlBarRef.current.offsetHeight - 2;
            setControlBarHeight(height);
        }
    };
    useEffect(() => {
        updateControlBarHeight();
        // Set current section identifier
        sessionStorage.setItem('lastSection', 'connections');
    }, [controlBarRef.current]);

    useEffect(() => {
        if (!workspace?._id) return;
        getCustomFieldDefinitionsHandler();
    }, [workspace?._id]); // eslint-disable-line react-hooks/exhaustive-deps -- handler identity changes each render

    // Use useLayoutEffect to set kanbanView before render
    useLayoutEffect(() => {
        // If coming from Tags page, force list view and save it to localStorage (only once)
        if (fromTagsPage && !hasProcessedFromTagsPage.current) {
            hasProcessedFromTagsPage.current = true;
            setKanbanView(false);
            localStorage.setItem('kanbanView', 'false'); // Save list view preference
            setTabs(prevTabs => prevTabs.map(tab => ({...tab, selected: tab.slug === 'list'})));
        }
    }, [fromTagsPage]);

    useEffect(() => {
        // Clear the flag from location state after component mounts
        if (fromTagsPage && hasProcessedFromTagsPage.current) {
            setTimeout(() => {
                const currentState = location.state || {};
                const { fromTagsPage: _, ...restState } = currentState;
                navigate(location.pathname, { replace: true, state: restState });
            }, 100);
        }
        
        // Only load from localStorage if not coming from Tags page
        if (!fromTagsPage && !hasProcessedFromTagsPage.current) {
            const isKanbanView = localStorage.getItem('kanbanView');
            if(isKanbanView === 'true') {
                setKanbanView(true);
                setTabs(prevTabs => prevTabs.map(tab => ({...tab, selected: tab.slug === 'kanban'})))
            }
        }

        window.addEventListener('resize', updateControlBarHeight);
        return () => {
            window.removeEventListener('resize', updateControlBarHeight);
        };
    }, [fromTagsPage, navigate, location.pathname, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (showCreateFeedGuide && !hasProcessedGuide.current) {
            hasProcessedGuide.current = true;
            
            // Check if modal was already shown
            const hasShownGuide = localStorage.getItem('createFeedGuideShown');

            if (!hasShownGuide) {
                // Add delay for smoother appearance
                guideTimerRef.current = setTimeout(() => { 
                    setCreateFeedGuideModal();
                    // Mark as shown
                    localStorage.setItem('createFeedGuideShown', 'true');
                }, 2000);
            } else {
                // Show CreateFeedModal with disabled "Add to existing feed" option
                setCreateFeedModal(undefined, true); // true = disable existing feed option
            }
        }
    }, [showCreateFeedGuide, setCreateFeedGuideModal, setCreateFeedModal]);

    // Separate useEffect for clearing state
    useEffect(() => {
        if (showCreateFeedGuide) {
            // Clear the state immediately to prevent multiple calls
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [showCreateFeedGuide, navigate, location.pathname]);

    useEffect(() => {
        if (showCreateFeedModal) {
            setCreateFeedModal(selectedConnectionId ? [selectedConnectionId] : undefined);
            
            // Select the connection in the table if selectedConnectionId is provided
            if (selectedConnectionId) {
                // Find the connection by ID and select it
                const connectionToSelect = connections.find((conn: IConnection) => conn._id === selectedConnectionId);
                if (connectionToSelect) {
                    setSelectedConnections([connectionToSelect]);
                    setSelectedConnectionsIdsHandler([selectedConnectionId]);
                }
            }
            
            // Clear the state to prevent modal from showing again on page refresh
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [showCreateFeedModal, selectedConnectionId, setCreateFeedModal, navigate, location.pathname, connections, setSelectedConnectionsIdsHandler]);

    const handleDataChange = (selectedConnections: IConnection[]) => {
        setSelectedConnections(selectedConnections);
        setSelectedConnectionsIdsHandler(selectedConnections.map(connection => connection._id));
    };

    const handleAllSelectedChange = (isAllSelected: boolean) => {
        setSelectedAllConnections(isAllSelected);
    };

    const toggleKanbanHandler = () => {
        const newView = !kanbanView;
        setKanbanView(newView);
        localStorage.setItem('kanbanView', String(newView));
    }

    const enrichConnection = (connection: IConnection, enrichmentType: EnrichmentType) => {
        // Determine which checkboxes should be checked based on enrichmentType
        const phoneChecked = enrichmentType === EnrichmentType.PHONE;
        const privateEmailChecked = enrichmentType === EnrichmentType.PRIVATE_EMAIL;
        const businessEmailChecked = enrichmentType === EnrichmentType.BUSINESS_EMAIL;

        setCalculateEnrichmentModalHandler(
            calculateEnrichmentHandler,
            onEnrichedTransactionChange,
            false,
            [connection],
            undefined,
            phoneChecked,
            privateEmailChecked,
            businessEmailChecked,
            false,
            undefined,
            true
        );
    }

    const [ tabs, setTabs ] = useState<ITabProperties[]>([{
            slug: 'list',
            title: 'List',
            iconType: 'list',
            selected: true
        },{
            slug: 'kanban',
            title: 'Pipeline',
            iconType: 'kanban',
            selected: false,
            ...(workspace?.isStarter && { disabled: true, tooltip: 'Upgrade your plan to unlock this feature' })
        },
    ])

    const onSwitchTab = useCallback((slug: string) =>{
        setKanbanView(slug === 'kanban');
        localStorage.setItem('kanbanView', String(slug === 'kanban'));
        setTabs(tabs.map(tab => ({...tab, selected: tab.slug === slug})))
    }, [tabs])

    const onBulkEnrich = (
        selectedConnections: IConnection[],
        privateEmailChecked: boolean,
        businessEmailChecked: boolean,
        phoneChecked: boolean,
        enrichedTransactionId: string
    ) => {
        let items: { connectionId: string, type: EnrichmentType }[] = [];
        selectedConnections.forEach(connection => {
            if (phoneChecked) {
                items.push({ connectionId: connection._id, type: EnrichmentType.PHONE });
            }
            if (privateEmailChecked) {
                items.push({ connectionId: connection._id, type: EnrichmentType.PRIVATE_EMAIL });
            }
            if (businessEmailChecked) {
                items.push({ connectionId: connection._id, type: EnrichmentType.BUSINESS_EMAIL });
            }
        });
        setEnrichingItems(items);
        setEnrichingTransactionId(enrichedTransactionId);
    }

    const onEnrichedTransactionChange = (enrichedTransaction: IEnrichedTransaction) => {
        setEnrichedTransaction(enrichedTransaction);
    }

    const handlePageChanged = (value: number) => {
        setConnectionsParametersHandler({ ...connectionsParameters, page: value });
    };

    const onSetSavedFilterHandler = () => {
        setSelectedConnections([]);
        setSelectedAllConnections(false);
    }

    const handleClearSelection = () => {
        setSelectedConnections([]);
        setSelectedAllConnections(false);
    }

    return <Container>
        {!kanbanView && 
            <HeightColumn>
                <TabsView borderBottom={false} tabs={tabs} onSwitchTab={(e) => onSwitchTab(e)}></TabsView>
                <StickyControlBar ref={controlBarRef}>
                    <ConnectionControlBar 
                        selectedConnections={selectedConnections} 
                        selectedAllConnections={selectedAllConnections} 
                        isListView={!kanbanView}
                        onBulkEnrich={onBulkEnrich}
                        onEnrichedTransactionChange={onEnrichedTransactionChange}
                        onPageChanged={handlePageChanged}
                        onPageSizeChanged={(value: number) => setConnectionsParametersHandler({ ...connectionsParameters, pageSize: value })}
                        onSetSavedFilter={onSetSavedFilterHandler}
                        onClearSelection={handleClearSelection}
                    />
                </StickyControlBar>
                <ConnectionTable 
                    controlBarHeight={controlBarHeight} 
                    selectedConnectionsChange={handleDataChange} 
                    selectedConnections={selectedConnections} 
                    selectedAllConnectionsChange={handleAllSelectedChange} 
                    enrichConnection={enrichConnection}
                    enrichingConnections={enrichingItems}
                    enrichingTransactionId={enrichingTransactionId}
                    enrichedTransaction={enrichedTransaction}
                />
            </HeightColumn>
        }
        {kanbanView && 
            <HeightColumn>
                <TabsView borderBottom={false} tabs={tabs} onSwitchTab={(e) => onSwitchTab(e)}></TabsView>
                <StickyControlBar ref={controlBarRef}>
                    <ConnectionControlBar selectedConnections={selectedConnections} selectedAllConnections={selectedAllConnections} isListView={!kanbanView} onClearSelection={handleClearSelection} />
                </StickyControlBar>
                <KanbanBoard selectedConnections={selectedConnections} />
            </HeightColumn>
        }
    </Container>
}

const Container = styled.div`
    width: 100%;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 0px;
    height: 100vh;
    overflow: hidden;
`

const StickyControlBar = styled.div`
    position: sticky;
    top: 0;
    z-index: 101;
    background: ${({theme: { tertiaryColor }}) => tertiaryColor};
    padding-top: 16px;
`;

const HeightColumn = styled(Column)`
    height: -webkit-fill-available;
`

export default ConnectionSection

